import type { ReactNode } from 'react';
import type { ConceptAppearance } from '@knolskape/educate-components';
import { KnolskapeLogo } from './KnolskapeLogo';

interface EducateNavHeaderProps {
  eyebrow: string;
  title: string;
  showLogo?: boolean;
  children: ReactNode;
  appearance?: ConceptAppearance;
}

const RAINBOW =
  'linear-gradient(90deg,#f5c518 0%,#ff7a00 20%,#ff2d55 40%,#8b5cf6 60%,#2563eb 80%,#16a34a 100%)';

export function EducateNavHeader({
  eyebrow,
  title,
  showLogo = true,
  children,
  appearance
}: EducateNavHeaderProps) {
  const navOverride = appearance?.overrides?.['header.nav'] ?? {};
  const rainbowOverride = appearance?.overrides?.['header.rainbow'];
  const eyebrowOverride = appearance?.overrides?.['header.eyebrow'] ?? {};
  const titleOverride = appearance?.overrides?.['header.title'] ?? {};
  const logoOverride = appearance?.overrides?.['header.logo'] ?? {};
  const pageBackground =
    (appearance?.overrides?.['background'] as { background?: string } | undefined)?.background ??
    appearance?.theme?.background;

  const logoMediaItem = appearance?.media?.['header.logo']?.find(
    (item) => item.url && item.kind === 'image'
  );
  const rawLogoSrc = appearance?.logo?.src;
  const logoSrc =
    logoMediaItem?.url ??
    (rawLogoSrc && rawLogoSrc !== '/logo.png' && rawLogoSrc !== '/knolskape_logo.png'
      ? rawLogoSrc
      : undefined);

  return (
    <div
      className="flex min-h-screen flex-col bg-slate-100 text-slate-900"
      style={pageBackground ? { background: pageBackground } : undefined}
    >
      <div className="sticky top-0 z-30 shadow-[0_4px_16px_-4px_rgba(15,23,42,0.12)]">
        <div
          className="h-1 w-full shrink-0"
          style={rainbowOverride ?? { background: RAINBOW }}
          aria-hidden="true"
        />

        <header className="shrink-0 border-b border-slate-200 bg-white" style={navOverride}>
          <div className="flex items-center justify-between gap-4 px-[15%] py-3.5">
            <div className="min-w-0">
              <p
                className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600"
                style={eyebrowOverride}
              >
                {eyebrow}
              </p>
              <h1
                className="mt-0.5 truncate text-xl font-bold text-slate-900 sm:text-2xl"
                style={titleOverride}
              >
                {title}
              </h1>
            </div>
            {showLogo &&
              (logoSrc ? (
                <div
                  className="hidden shrink-0 sm:block"
                  style={{
                    height: 44,
                    width: 160,
                    backgroundImage: `url("${logoSrc}")`,
                    backgroundSize:
                      logoMediaItem?.fit === 'fill'
                        ? '100% 100%'
                        : (logoMediaItem?.fit ?? 'contain'),
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    ...logoOverride
                  }}
                />
              ) : (
                <KnolskapeLogo className="hidden shrink-0 sm:flex" />
              ))}
          </div>
        </header>
      </div>

      {children}
    </div>
  );
}
